import { api } from '../../api/client';
import { fetchAllPages, toCsv } from '../../lib/csv';
import { formatHandle, listenerLabel, validDisplayName } from '../../lib/listenerName';
import { callsCsv, type CallRow } from '../calls/CallsPage';
import type { ConsoleMetrics } from '../dashboard/DashboardPage';
import type { AdminPayoutRow } from '../payouts/payoutViews';
import type { StatsRangeParams } from '../listeners/listenerStats';
import { tempTrackingCsv, type TempTrackingReport } from '../temp-tracking/tempTracking';
import { workDateRange } from '../listeners/employment/employmentForm';
import {
  employmentAttendanceCsv,
  employmentEarningsCsv,
  employmentOvertimeCsv,
  employmentPayrollCsv,
  sumSettlements,
  type SettledByEmployment,
} from '../listeners/employment/attendanceCsv';
import type { ReportItem, ReportResponse, SettlementListResponse, SettlementRow } from '../listeners/employment/employmentTypes';

/**
 * The central report catalogue. Each report builds a CSV for the selected
 * period from the SAME endpoints the pages use — no report-only queries, so a
 * report and the page it summarises always agree. Listener columns are always
 * the admin display name (plus the @handle to identify the account).
 */

/** A built report: text to save and how many data rows it holds. */
export interface BuiltReport {
  csv: string;
  rows: number;
  /** More rows exist than `maxRows` — narrow the range for the rest. */
  truncated: boolean;
}

export interface ReportDef {
  key: string;
  title: string;
  description: string;
  /** The permission of the data it exports (the API enforces the same one). */
  permission: string;
  /** Extra filter this report offers. */
  filter?: 'callOutcome' | 'rechargeStatus' | 'payoutStatus' | 'settlementMode';
  /** `maxRows` small = a preview; large = the download. */
  build: (range: StatsRangeParams, opts: { maxRows: number; filter?: string }) => Promise<BuiltReport>;
}

/** Most rows one download fetches. */
export const REPORT_MAX_ROWS = 10_000;
export const PREVIEW_ROWS = 8;

const rupees = (paise: string | number | bigint) => (Number(paise) / 100).toFixed(2);
const mins = (seconds: number) => (seconds / 60).toFixed(1);

/** Walk a page-numbered endpoint (`page` / `pageSize` / `total`). */
async function fetchAllNumbered<T>(
  fetchPage: (page: number, pageSize: number) => Promise<{ items: T[]; total: number }>,
  maxRows: number,
): Promise<{ rows: T[]; truncated: boolean }> {
  const pageSize = Math.min(100, maxRows);
  const rows: T[] = [];
  for (let page = 1; ; page++) {
    const res = await fetchPage(page, pageSize);
    rows.push(...res.items);
    if (rows.length >= res.total || res.items.length === 0) return { rows, truncated: false };
    if (rows.length >= maxRows) return { rows: rows.slice(0, maxRows), truncated: true };
  }
}

interface OrderRow {
  id: string;
  userId: string;
  userHandle?: string;
  /** The paying user's own Display Name — absent on older APIs. */
  userDisplayName?: string | null;
  amountPaise: string;
  gateway: string;
  gatewayOrderId: string;
  packageName: string | null;
  coins: number | null;
  bonusCoins: number;
  status: string;
  stage: string;
  createdAt: string;
  capturedAt?: string | null;
}

interface ListenerStatRow {
  id: string;
  displayName: string;
  status: string;
  user: { handle: string };
  view: { modeSeconds?: number } | null;
  stats: {
    online: { onlineSeconds: number } | null;
    calls: { calls: number; connectedCalls: number; callSeconds: number };
    money: { callerCoins: number; earningsPaise: string; callEarningsPaise: string } | null;
  };
}

/** One row of the listener performance report. */
export interface ListenerPerformanceRow {
  id: string;
  /** The admin label (Display Name, else @handle, else short id) — for plain text. */
  listener: string;
  /** The raw listener Display Name (null when unset) — for rendering the name rule. */
  displayName: string | null;
  handle: string;
  calls: number;
  connectedCalls: number;
  audioSeconds: number;
  videoSeconds: number;
  totalSeconds: number;
  onlineSeconds: number | null;
  callerCoins: number | null;
  earningsPaise: string | null;
}

/** Approved listeners with their period figures; audio/video split from the two rankings. */
export async function listenerPerformance(
  range: StatsRangeParams,
  maxRows: number,
): Promise<{ rows: ListenerPerformanceRow[]; truncated: boolean }> {
  const list = (extra: Record<string, string>) =>
    fetchAllNumbered<ListenerStatRow>(
      async (page, pageSize) =>
        (
          await api.get<{ items: ListenerStatRow[]; total: number }>('/admin/listeners', {
            params: { ...extra, page, pageSize, ...range },
          })
        ).data,
      maxRows,
    );
  const [all, audio, video] = await Promise.all([
    list({ status: 'approved', sort: 'name' }),
    list({ view: 'top_audio' }),
    list({ view: 'top_video' }),
  ]);
  const audioSecs = new Map(audio.rows.map((r) => [r.id, r.view?.modeSeconds ?? 0]));
  const videoSecs = new Map(video.rows.map((r) => [r.id, r.view?.modeSeconds ?? 0]));
  return {
    truncated: all.truncated,
    rows: all.rows.map((l) => ({
      id: l.id,
      listener: listenerLabel({ displayName: l.displayName, handle: l.user.handle, id: l.id }),
      displayName: validDisplayName(l.displayName),
      handle: l.user.handle,
      calls: l.stats.calls.calls,
      connectedCalls: l.stats.calls.connectedCalls,
      audioSeconds: audioSecs.get(l.id) ?? 0,
      videoSeconds: videoSecs.get(l.id) ?? 0,
      totalSeconds: l.stats.calls.callSeconds,
      onlineSeconds: l.stats.online?.onlineSeconds ?? null,
      callerCoins: l.stats.money?.callerCoins ?? null,
      earningsPaise: l.stats.money?.earningsPaise ?? null,
    })),
  };
}

export function listenerPerformanceCsv(rows: ListenerPerformanceRow[]): string {
  return toCsv(
    ['listener', 'handle', 'listener_id', 'calls', 'connected_calls', 'audio_minutes', 'video_minutes', 'total_minutes', 'online_hours', 'caller_coins', 'earnings_rupees'],
    rows.map((r) => [
      r.listener,
      formatHandle(r.handle) ?? '',
      r.id,
      r.calls,
      r.connectedCalls,
      mins(r.audioSeconds),
      mins(r.videoSeconds),
      mins(r.totalSeconds),
      r.onlineSeconds === null ? '' : (r.onlineSeconds / 3600).toFixed(2),
      r.callerCoins ?? '',
      r.earningsPaise === null ? '' : rupees(r.earningsPaise),
    ]),
  );
}

export function payoutsCsv(rows: AdminPayoutRow[]): string {
  return toCsv(
    ['payout_id', 'requested_at', 'listener', 'handle', 'listener_id', 'gross_rupees', 'tds_rupees', 'net_rupees', 'status', 'approved_at', 'paid_at', 'payment_reference'],
    rows.map((p) => [
      p.id,
      p.createdAt,
      listenerLabel({ displayName: p.listener.displayName, handle: p.listener.user.handle, id: p.listener.id }),
      formatHandle(p.listener.user.handle) ?? '',
      p.listener.id,
      rupees(p.amountPaise),
      rupees(p.tdsPaise),
      rupees(p.netPayoutPaise),
      p.status,
      p.approvedAt ?? '',
      p.settledAt ?? '',
      p.paymentReference ?? '',
    ]),
  );
}

export function rechargesCsv(rows: OrderRow[]): string {
  return toCsv(
    ['order_id', 'created_at', 'user', 'user_display_name', 'user_id', 'amount_rupees', 'coins', 'bonus_coins', 'package', 'gateway', 'gateway_order_id', 'status', 'stage'],
    rows.map((o) => [
      o.id,
      o.createdAt,
      formatHandle(o.userHandle) ?? '',
      validDisplayName(o.userDisplayName) ?? '',
      o.userId,
      rupees(o.amountPaise),
      o.coins ?? '',
      o.bonusCoins,
      o.packageName ?? '',
      o.gateway,
      o.gatewayOrderId,
      o.status,
      o.stage,
    ]),
  );
}

/** The period summary as a two-column sheet (metric, value). */
export function revenueCsv(m: ConsoleMetrics): string {
  return toCsv(
    ['metric', 'value'],
    [
      ['period_from', m.range.from],
      ['period_to', m.range.to],
      ['recharge_amount_rupees', rupees(m.money.rechargePaise)],
      ['captured_recharges', m.money.recharges],
      ['users_who_recharged', m.users.usersWhoRecharged],
      ['coins_purchased', m.money.coinsPurchased],
      ['bonus_coins', m.money.bonusCoins],
      ['listener_earnings_rupees', rupees(m.money.listenerEarningsPaise)],
      ['platform_margin_rupees', rupees(m.money.platformMarginPaise)],
      ['avg_revenue_per_call_rupees', m.money.avgRevenuePerCallPaise === null ? '' : rupees(m.money.avgRevenuePerCallPaise)],
      ['total_calls', m.calls.total],
      ['completed_calls', m.calls.completed],
      ['missed_calls', m.calls.missed],
      ['declined_calls', m.calls.declined],
      ['cancelled_calls', m.calls.cancelled],
      ['failed_calls', m.calls.failed],
      ['audio_minutes', mins(m.calls.audioSeconds)],
      ['video_minutes', mins(m.calls.videoSeconds)],
      ['total_minutes', mins(m.calls.totalSeconds)],
      ['coins_charged_in_calls', m.calls.coinsCharged],
      ['new_users', m.users.newUsers],
      ['total_users', m.users.totalUsers],
      ['approved_listeners', m.listeners.approved],
      ['new_listener_applications', m.listeners.newApplications],
      ['rejected_applications', m.listeners.rejected],
    ],
  );
}

/**
 * Every employee's period figures from `/admin/employment/report`. The
 * employment API wants the employee's WORK DATES (`YYYY-MM-DD`, inclusive),
 * not the pickers' ISO instants, so the range is converted first.
 */
export async function employmentReport(
  range: StatsRangeParams,
  maxRows: number,
  extra: Record<string, string | undefined> = {},
): Promise<{ rows: ReportItem[]; truncated: boolean; from: string; to: string }> {
  const { from, to } = workDateRange(range);
  const { rows, truncated } = await fetchAllNumbered<ReportItem>(
    async (page, pageSize) =>
      (await api.get<ReportResponse>('/admin/employment/report', { params: { from, to, ...extra, page, pageSize } })).data,
    maxRows,
  );
  return { rows, truncated, from, to };
}

/**
 * Σ credited / pending wallet settlements per employment for the period
 * (`/admin/employment/settlements`), for the payroll-ready report. A failure
 * here must not lose the payroll figures: the two columns are then blank.
 */
async function settledByEmployment(from: string, to: string, maxRows: number): Promise<SettledByEmployment> {
  try {
    const { rows } = await fetchAllNumbered<SettlementRow>(
      async (page, pageSize) =>
        (await api.get<SettlementListResponse>('/admin/employment/settlements', { params: { from, to, page, pageSize } })).data,
      maxRows,
    );
    return sumSettlements(rows);
  } catch {
    return {};
  }
}

export const REPORTS: ReportDef[] = [
  {
    key: 'calls',
    title: 'Calls',
    description: 'Every call started in the period: who called whom, mode, outcome, duration and coins charged.',
    permission: 'calls.view',
    filter: 'callOutcome',
    build: async (range, { maxRows, filter }) => {
      const { rows, truncated } = await fetchAllPages<CallRow>(
        async (cursor) =>
          (
            await api.get<{ items: CallRow[]; nextCursor: string | null }>('/admin/calls', {
              params: { since: range.from, until: range.to, outcome: filter || undefined, cursor, limit: Math.min(100, maxRows) },
            })
          ).data,
        maxRows,
      );
      return { csv: callsCsv(rows), rows: rows.length, truncated };
    },
  },
  {
    key: 'recharges',
    title: 'Coin purchases',
    description: 'Every coin purchase attempt started in the period, with its amount, coins, gateway, status and stage.',
    permission: 'payments.view',
    filter: 'rechargeStatus',
    build: async (range, { maxRows, filter }) => {
      const { rows, truncated } = await fetchAllPages<OrderRow>(
        async (cursor) =>
          (
            await api.get<{ items: OrderRow[]; nextCursor: string | null }>('/admin/payments/orders', {
              params: { since: range.from, until: range.to, status: filter || undefined, cursor, limit: Math.min(100, maxRows) },
            })
          ).data,
        maxRows,
      );
      return { csv: rechargesCsv(rows), rows: rows.length, truncated };
    },
  },
  {
    key: 'earnings',
    title: 'Listener earnings & performance',
    description:
      'Per approved listener for the period: calls, audio / video / total minutes, online time, coins callers spent and ₹ earned.',
    permission: 'listeners.view',
    build: async (range, { maxRows }) => {
      const { rows, truncated } = await listenerPerformance(range, maxRows);
      return { csv: listenerPerformanceCsv(rows), rows: rows.length, truncated };
    },
  },
  {
    key: 'payouts',
    title: 'Payouts',
    description: 'Payout requests made in the period: gross, TDS, net, status and who approved / paid them.',
    permission: 'payouts.view',
    filter: 'payoutStatus',
    build: async (range, { maxRows, filter }) => {
      const { rows, truncated } = await fetchAllNumbered<AdminPayoutRow>(
        async (page, pageSize) =>
          (
            await api.get<{ items: AdminPayoutRow[]; total: number }>('/admin/payouts/history', {
              params: { from: range.from, to: range.to, status: filter || undefined, page, pageSize },
            })
          ).data,
        maxRows,
      );
      return { csv: payoutsCsv(rows), rows: rows.length, truncated };
    },
  },
  {
    key: 'revenue',
    title: 'Revenue summary',
    description:
      'The period at a glance: recharge amount, coins purchased, listener earnings, platform margin, calls and minutes.',
    permission: 'dashboard.view',
    build: async (range) => {
      const m = (await api.get<ConsoleMetrics>('/admin/console/metrics', { params: range })).data;
      const csv = revenueCsv(m);
      return { csv, rows: csv.split('\n').length - 1, truncated: false };
    },
  },
  {
    key: 'employment_attendance',
    title: 'Employee attendance',
    description: 'Per salaried listener for the period: days, scheduled / presence / worked / regular / overtime / missing hours.',
    permission: 'listeners.employment.view',
    build: async (range, { maxRows }) => {
      const { rows, truncated } = await employmentReport(range, maxRows);
      return { csv: employmentAttendanceCsv(rows), rows: rows.length, truncated };
    },
  },
  {
    key: 'employment_earnings',
    title: 'Employee salary earnings',
    description: 'Per salaried listener for the period: base, overtime and total ₹ (blank without the pay permission), with the rate model, basis, schedule type and wallet settlement mode.',
    permission: 'listeners.employment.view',
    filter: 'settlementMode',
    build: async (range, { maxRows, filter }) => {
      const { rows, truncated } = await employmentReport(range, maxRows, { settlementMode: filter || undefined });
      return { csv: employmentEarningsCsv(rows), rows: rows.length, truncated };
    },
  },
  {
    key: 'employment_overtime',
    title: 'Employee overtime',
    description: 'Salaried listeners with overtime or unpaid extra time in the period, and what the overtime pays.',
    permission: 'listeners.employment.view',
    build: async (range, { maxRows }) => {
      const { rows, truncated } = await employmentReport(range, maxRows);
      const csv = employmentOvertimeCsv(rows);
      return { csv, rows: csv.split('\n').length - 1, truncated };
    },
  },
  {
    key: 'employment_payroll_ready',
    title: 'Employee payroll (ready to pay)',
    description: 'One line per active employee for the period, ready for a payroll sheet: hours, base, overtime and total ₹, plus what already reached the wallet and what still waits.',
    permission: 'listeners.employment.view',
    filter: 'settlementMode',
    build: async (range, { maxRows, filter }) => {
      const { rows, truncated, from, to } = await employmentReport(range, maxRows, { status: 'active', settlementMode: filter || undefined });
      const settled = rows.length > 0 ? await settledByEmployment(from, to, maxRows * 40) : {};
      return { csv: employmentPayrollCsv(rows, from, to, settled), rows: rows.length, truncated };
    },
  },
  {
    key: 'compensation',
    title: 'Online-time compensation',
    description:
      'Per listener: recorded online time, online time the history missed (older app builds), and the merged total.',
    permission: 'listeners.activity.view',
    build: async (range, { maxRows }) => {
      const report = (await api.get<TempTrackingReport>('/admin/listeners/temp-tracking', { params: range })).data;
      const rows = report.rows.slice(0, maxRows);
      return {
        csv: tempTrackingCsv({ ...report, rows }),
        rows: rows.length,
        truncated: report.rows.length > rows.length,
      };
    },
  },
];

/** Options of each report's own filter. */
export const REPORT_FILTERS: Record<NonNullable<ReportDef['filter']>, Array<{ value: string; label: string }>> = {
  callOutcome: [
    { value: 'connected', label: 'Completed (connected)' },
    { value: 'unconnected', label: 'Never connected' },
    { value: 'no_answer', label: 'Missed — no answer' },
    { value: 'declined', label: 'Declined' },
    { value: 'cancelled', label: 'Cancelled' },
  ],
  rechargeStatus: [
    { value: 'captured', label: 'Captured' },
    { value: 'failed', label: 'Failed' },
    { value: 'paid_credit_pending', label: 'Paid, not credited' },
    { value: 'refunded', label: 'Refunded' },
    { value: 'abandoned', label: 'Abandoned' },
  ],
  payoutStatus: [
    { value: 'pending_approval', label: 'Pending approval' },
    { value: 'approved', label: 'Approved' },
    { value: 'settled', label: 'Paid' },
    { value: 'rejected', label: 'Rejected' },
  ],
  settlementMode: [
    { value: 'automatic', label: 'Automatic wallet credit' },
    { value: 'manual', label: 'Manual (admin credits)' },
  ],
};

/** A CSV's first rows as a table: header + up to `limit` data rows. */
export function previewOf(csv: string, limit = PREVIEW_ROWS): { header: string[]; rows: string[][] } {
  const lines = parseCsv(csv);
  return { header: lines[0] ?? [], rows: lines.slice(1, 1 + limit) };
}

/** Minimal CSV reader for our own output (quoted cells, doubled quotes). */
export function parseCsv(text: string): string[][] {
  const out: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (quoted) {
      if (ch === '"' && text[i + 1] === '"') {
        cell += '"';
        i++;
      } else if (ch === '"') quoted = false;
      else cell += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === ',') {
      row.push(cell);
      cell = '';
    } else if (ch === '\n') {
      row.push(cell);
      out.push(row);
      row = [];
      cell = '';
    } else if (ch !== '\r') cell += ch;
  }
  if (cell !== '' || row.length > 0) {
    row.push(cell);
    out.push(row);
  }
  return out;
}
